import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "./skeleton";

interface LoadingStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual style to render. Defaults to full-page spinner. */
  variant?: "spinner" | "skeleton" | "inline";
  /** Optional message shown beneath the loader. */
  message?: string;
  /** Number of placeholder rows for the skeleton variant. */
  rows?: number;
}

export function LoadingState({
  variant = "spinner",
  message = "Loading...",
  rows = 6,
  className,
  ...props
}: LoadingStateProps) {
  if (variant === "inline") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 text-sm text-gray-500",
          className
        )}
        {...props}
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span>{message}</span>
      </div>
    );
  }

  if (variant === "skeleton") {
    return (
      <div className={cn("space-y-4", className)} {...props}>
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="rounded-md border">
          <div className="grid grid-cols-4 gap-2 border-b p-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-12 justify-self-end" />
          </div>
          <div className="divide-y">
            {Array.from({ length: rows }).map((_, index) => (
              <div key={index} className="p-3">
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex min-h-[200px] flex-col items-center justify-center text-gray-500",
        className
      )}
      {...props}
    >
      <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      <p className="mt-3 text-base">{message}</p>
    </div>
  );
}
